import { useState } from 'react';
import { Box, Button, Collapse, IconButton, List, ListItem, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import FileContextForm, {
  EMPTY_FILE_CONTEXT_FORM_VALUE,
  FileContextFormValue
} from './FileContextForm';
import { formatByteSize } from './upload_validation';

/**
 * One line summarising the hints a file's context sets, or null when none are.
 *
 * The client is deliberately omitted: only its id is in form state, and resolving
 * the display name would mean a second `fetchEntities` call from a component that
 * otherwise needs no data. It is chosen once, directly beside this list, where it
 * stays visible anyway.
 * @param {FileContextFormValue} context The file's context form value.
 * @returns {string | null} A middot-separated summary, or null when nothing is set.
 */
export function summariseContext(context: FileContextFormValue): string | null {
  const parts: Array<string> = [];
  if (context.entityHint.trim() !== '') {
    parts.push(context.entityHint.trim());
  }
  if (context.periodHint.trim() !== '') {
    parts.push(context.periodHint.trim());
  }
  if (context.unitHint.trim() !== '') {
    parts.push(context.unitHint.trim());
  }
  if (context.metricHints.length > 0) {
    parts.push(context.metricHints.join(', '));
  }
  if (context.contextNote.trim() !== '') {
    parts.push('a note');
  }
  // The Replaces choice is not a hint — it changes how the file is ingested — so a
  // collapsed row must not hide it (Maxine R5 ④). Only the id is known here, so the
  // summary names the fact, not the file.
  if (context.supersedesFileId.trim() !== '') {
    parts.push('replaces an earlier file');
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** True when any field of a file's context carries a value. */
function hasContext(context: FileContextFormValue | undefined): boolean {
  return context !== undefined && summariseContext(context) !== null;
}

interface SelectedFileListProps {
  /** Locally selected files awaiting upload (filenames are unique by validation). */
  files: Array<File>;
  /** Per-file context form values, keyed by filename. */
  overrides: Record<string, FileContextFormValue>;
  /** Called when a file's context form changes. */
  onOverrideChange: (filename: string, value: FileContextFormValue) => void;
  /** Remove a file from the selection (before any upload happens). */
  onRemove: (filename: string) => void;
  /** Disable all interaction (e.g. while the batch upload is in flight). */
  disabled?: boolean;
}

/**
 * The pre-upload selection list: one row per chosen file with its size, a
 * remove button and an expandable per-file context form. One context per file is
 * the whole model — the batch-wide default form is gone (Maxine R4 ④); only the
 * batch-wide Client survives, beside the list (see ClientField).
 * @param {Array<File>} files Locally selected files.
 * @param {Record<string, FileContextFormValue>} overrides Context form values by filename.
 * @param {(filename: string, value: FileContextFormValue) => void} onOverrideChange Context-change handler.
 * @param {(filename: string) => void} onRemove Remove-file handler.
 * @param {boolean | undefined} disabled Disable all interaction.
 * @returns {JSX.Element}
 */
export default function SelectedFileList({
  files,
  overrides,
  onOverrideChange,
  onRemove,
  disabled
}: SelectedFileListProps) {
  const [expandedFilename, setExpandedFilename] = useState<string | null>(null);

  const toggleExpanded = (filename: string): void => {
    setExpandedFilename((current) => (current === filename ? null : filename));
  };

  return (
    <List disablePadding>
      {files.map((file: File) => {
        const expanded = expandedFilename === file.name;
        const contextSet = hasContext(overrides[file.name]);
        return (
          <Box key={file.name} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <ListItem
              disableGutters
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Was a bare chevron behind a hover tooltip — an invisible
                      affordance for the only per-file control in the flow. */}
                  <Button
                    size="small"
                    onClick={() => toggleExpanded(file.name)}
                    disabled={disabled}
                    aria-label={`Show context for ${file.name}`}
                    endIcon={
                      expanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    {/* "Override" named the mechanism, not the job: a reviewer could
                        not tell what it would change (Maxine ②). The label now names
                        what is behind it — this file's own context. */}
                    {contextSet ? 'Edit file context' : 'File context'}
                  </Button>
                  <Tooltip title="Remove file">
                    <IconButton
                      size="small"
                      onClick={() => onRemove(file.name)}
                      disabled={disabled}
                      aria-label={`Remove ${file.name}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              <Box sx={{ minWidth: 0, pr: 2 }}>
                <BodyText weight="Medium">{file.name}</BodyText>
                <CaptionText>
                  {formatByteSize(file.size)}
                  {/* The summary names the hints this file's context sets, so a
                      collapsed row still shows what will ride along with it. */}
                  {contextSet && ` · Context: ${summariseContext(overrides[file.name])}`}
                </CaptionText>
              </Box>
            </ListItem>
            <Collapse in={expanded} unmountOnExit>
              <Box sx={{ pb: 2, pl: 1 }}>
                <CaptionText sx={{ mb: 1, display: 'block' }}>
                  Context for this file only. Every field is a hint — skip any you are unsure about;
                  we read what we can from the file itself. This is also where you say the file
                  replaces an earlier upload.
                </CaptionText>
                <FileContextForm
                  value={overrides[file.name] ?? EMPTY_FILE_CONTEXT_FORM_VALUE}
                  onChange={(value: FileContextFormValue) => onOverrideChange(file.name, value)}
                  disabled={disabled}
                />
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </List>
  );
}
